// src/components/MyListings/MyListings.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaShoppingCart, FaMoneyBillWave, FaLink, FaCopy, FaCheckCircle, FaTimesCircle,
  FaBell, FaExclamationTriangle, FaPlus, FaClock, FaChartBar, FaTrophy, FaFire,
  FaEye, FaChevronUp, FaChevronDown, FaPen, FaPlay, FaPause, FaTrashAlt, FaMedal,
  FaSpinner, FaMinus,
} from 'react-icons/fa';
import './MyListings.css';

import { apiFetch } from '../../services/apiClient';
import { useAuth } from '../../Context/AuthContext';
import FeatureOfferButton from '../Featured/FeatureOfferButton';

const PRIVY_ENABLED = !!process.env.REACT_APP_PRIVY_APP_ID;

// ── Redes / monedas ───────────────────────────────────────────────
const CHAIN_COLORS = {
  Bitcoin: '#f7931a', Ethereum: '#627eea', BNB: '#f0b90b', BSC: '#f0b90b',
  Solana: '#9945ff', Litecoin: '#bfbbbb', TRON: '#ef0027',
};
const CRYPTO_ICONS = {
  BTC: '/assets/crypto-logos/btc.svg',
  ETH: '/assets/crypto-logos/eth.svg',
  BNB: '/assets/crypto-logos/bnb.svg',
  SOL: '/assets/crypto-logos/sol.svg',
  LTC: '/assets/crypto-logos/ltc.svg',
  TRX: '/assets/crypto-logos/trx.svg',
  USDT: '/assets/crypto-logos/usdt.svg',
  USDC: '/assets/crypto-logos/usdc.svg',
};
const CRYPTO_COLORS = { USDT: '#26a17b', USDC: '#2775ca', BTC: '#f7931a', ETH: '#627eea', BNB: '#f0b90b', SOL: '#9945ff', LTC: '#bfbbbb', TRX: '#ef0027' };

function chainCoin(name) {
  const n = String(name || '').toLowerCase();
  if (/bitcoin|btc/.test(n))      return 'BTC';
  if (/ethereum|eth|erc/.test(n)) return 'ETH';
  if (/bsc|bnb|binance/.test(n))  return 'BNB';
  if (/solana|sol/.test(n))       return 'SOL';
  if (/litecoin|ltc/.test(n))     return 'LTC';
  if (/tron|trx|trc/.test(n))     return 'TRX';
  return null;
}

function CoinLogo({ symbol, size = 22, className = '' }) {
  const sym   = String(symbol || '').toUpperCase();
  const src   = CRYPTO_ICONS[sym];
  const color = CRYPTO_COLORS[sym] || 'var(--ml-text-tertiary)';
  return (
    <span className={`ml-coin-logo ${className}`} style={{ width: size, height: size }} title={sym}>
      {src && (
        <img src={src} alt={sym} width={size} height={size}
          onError={e => { e.currentTarget.style.display = 'none'; const f = e.currentTarget.nextSibling; if (f) f.style.display = 'flex'; }} />
      )}
      <span className="ml-coin-logo-fb" style={{ display: src ? 'none' : 'flex', background: color, width: size, height: size }}>
        {sym[0] || '?'}
      </span>
    </span>
  );
}

function ChainGlyph({ chain }) {
  const coin = chainCoin(chain);
  if (coin) return <CoinLogo symbol={coin} size={14} className="ml-chain-logo" />;
  return <span className="ml-chain-dot" style={{ background: CHAIN_COLORS[chain] ?? 'var(--ml-text-tertiary)' }} aria-hidden="true" />;
}

// ── Moneda local (ligero) ─────────────────────────────────────────
const COUNTRY_CURRENCY = {
  CR: { code: 'CRC', sym: '₡' }, CO: { code: 'COP', sym: '$' }, VE: { code: 'VES', sym: 'Bs' },
  MX: { code: 'MXN', sym: '$' }, AR: { code: 'ARS', sym: '$' }, PE: { code: 'PEN', sym: 'S/' },
  CL: { code: 'CLP', sym: '$' }, GT: { code: 'GTQ', sym: 'Q' }, US: { code: 'USD', sym: '$' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor(diff / 3600000);
  const mins  = Math.floor(diff / 60000);
  if (days >= 30)  return `hace ${Math.floor(days / 30)} mes${Math.floor(days/30) > 1 ? 'es' : ''}`;
  if (days >= 1)   return `hace ${days} día${days > 1 ? 's' : ''}`;
  if (hours >= 1)  return `hace ${hours}h`;
  return `hace ${Math.max(mins, 0)}m`;
}

// Precio efectivo de 1 USDT según el spread (estable ≈ $1)
const effPrice = (spread) => 1 * (1 + (Number(spread) || 0) / 100);
const spreadLabel = (s) => (s === 0 ? 'TRM' : s > 0 ? `+${s}%` : `${s}%`);

export default function MyListings() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [offers, setOffers]             = useState([]);
  const [market, setMarket]             = useState([]);     // ofertas públicas → competitividad
  const [loading, setLoading]           = useState(true);
  const [busyId, setBusyId]             = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [filter, setFilter]             = useState('all');
  const [sort, setSort]                 = useState('recent');
  const [showLocal, setShowLocal]       = useState(false);
  const [rate, setRate]                 = useState(null);   // fiat por USD
  const [editing, setEditing]           = useState(null);   // oferta en edición
  const [confirmDel, setConfirmDel]     = useState(null);   // id a borrar

  const cc = COUNTRY_CURRENCY[user?.country] || null;

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/offers/me');
        if (res.ok) setOffers(await res.json());
      } catch {}
      setLoading(false);
    })();
    // Ofertas públicas para comparar precio (no bloquea la carga)
    (async () => {
      try {
        const res = await apiFetch('/api/offers');
        if (res.ok) setMarket(await res.json());
      } catch {}
    })();
  }, []);

  // Tasa fiat para moneda local (solo si hace falta)
  useEffect(() => {
    if (!showLocal || !cc || rate != null) return;
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(d => { if (d?.rates?.[cc.code]) setRate(d.rates[cc.code]); })
      .catch(() => {});
  }, [showLocal, cc, rate]);

  // ── Acciones ────────────────────────────────────────────────────
  const patchLocal = (id, patch) => setOffers(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));

  const setActive = useCallback(async (id, next) => {
    setBusyId(id);
    try {
      const res = await apiFetch(`/api/offers/${id}/${next ? 'activate' : 'deactivate'}`, { method: 'PATCH' });
      if (res.ok)      patchLocal(id, { active: next });
      else { const e = await res.json().catch(() => ({})); alert(e.message || 'No se pudo cambiar el estado'); }
    } catch { alert('Error de conexión'); }
    setBusyId(null);
  }, []);

  const doDelete = useCallback(async (id) => {
    setBusyId(id);
    try {
      const res = await apiFetch(`/api/offers/${id}`, { method: 'DELETE' });
      if (res.ok) setOffers(prev => prev.filter(o => o.id !== id));
      else alert('No se pudo borrar');
    } catch { alert('Error de conexión'); }
    setBusyId(null);
    setConfirmDel(null);
  }, []);

  const quickSpread = useCallback(async (o, delta) => {
    const next = Math.round((Number(o.spread || 0) + delta) * 10) / 10;
    if (next < -10 || next > 20) return;
    patchLocal(o.id, { spread: next });        // optimista
    try {
      const res = await apiFetch(`/api/offers/${o.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spread: next }),
      });
      if (!res.ok) patchLocal(o.id, { spread: o.spread }); // revertir
    } catch { patchLocal(o.id, { spread: o.spread }); }
  }, []);

  const saveEdit = useCallback(async (form) => {
    setBusyId(form.id);
    try {
      const res = await apiFetch(`/api/offers/${form.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) { const updated = await res.json(); patchLocal(form.id, updated); setEditing(null); }
      else { const e = await res.json().catch(() => ({})); alert(e.message || 'No se pudo guardar'); }
    } catch { alert('Error de conexión'); }
    setBusyId(null);
  }, []);

  const duplicate = (o) => {
    const params = new URLSearchParams({
      type: o.type, amount: o.amount, minAmount: o.minAmount ?? 0, maxAmount: o.maxAmount ?? 0,
      spread: o.spread, network: o.network, payment: o.payment, responseTime: o.responseTime,
    });
    navigate(`/create?${params.toString()}`);
  };

  // ── Competitividad: ranking de mi spread vs el mercado (mismo tipo) ──
  const rankOf = useCallback((o) => {
    const peers = market.filter(m => m.type === o.type);
    if (peers.length < 2) return null;
    // Venta: precio más bajo gana (atrae compradores). Compra: precio más alto gana (atrae vendedores).
    const better = peers.filter(m => o.type === 'sell'
      ? Number(m.spread) < Number(o.spread)
      : Number(m.spread) > Number(o.spread)).length;
    return { rank: better + 1, total: peers.length };
  }, [market]);

  // ── Stats ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active   = offers.filter(o => o.active);
    const inactive = offers.filter(o => !o.active);
    const totalVolume = active.reduce((s, o) => s + Number(o.amount ?? 0), 0);
    const buyVolume   = active.filter(o => o.type === 'buy').reduce((s, o) => s + Number(o.amount ?? 0), 0);
    const sellVolume  = active.filter(o => o.type === 'sell').reduce((s, o) => s + Number(o.amount ?? 0), 0);
    return { active: active.length, inactive: inactive.length, totalVolume, buyVolume, sellVolume };
  }, [offers]);

  const alerts = useMemo(() => {
    const result = [];
    const now = Date.now();
    offers.forEach(o => {
      const days = Math.floor((now - new Date(o.createdAt).getTime()) / 86400000);
      if (o.active && days >= 14)
        result.push({ type: 'stale', msg: `Tu anuncio de USDT ${Number(o.amount).toLocaleString()} lleva ${days} días sin actualizarse. Revisá el precio.` });
    });
    if (offers.filter(o => o.active && o.type === 'sell').length > 0 && user && !user.verified)
      result.push({ type: 'inactive', msg: 'Tenés anuncios de venta pero tu cuenta no está verificada — no se muestran en el marketplace hasta verificarte.' });
    if (offers.length > 0 && offers.filter(o => o.active).length === 0)
      result.push({ type: 'inactive', msg: 'No tenés anuncios activos — no estás visible en el marketplace.' });
    return result;
  }, [offers, user]);

  const [dismissed, setDismissed] = useState([]);
  const visibleAlerts = alerts.filter((_, i) => !dismissed.includes(i));

  // ── Filtro + orden ──────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = offers.filter(o => filter === 'all' ? true : o.type === filter);
    if (sort === 'recent')      list = [...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (sort === 'price')       list = [...list].sort((a, b) => Number(a.spread) - Number(b.spread));
    if (sort === 'amount')      list = [...list].sort((a, b) => Number(b.amount) - Number(a.amount));
    return list;
  }, [offers, filter, sort]);

  const active   = filtered.filter(o => o.active);
  const inactive = filtered.filter(o => !o.active);

  // ── Card ────────────────────────────────────────────────────────
  const OfferCard = ({ o }) => {
    const days    = Math.floor((Date.now() - new Date(o.createdAt).getTime()) / 86400000);
    const isStale = o.active && days >= 14;
    const price   = effPrice(o.spread);
    const local   = showLocal && rate && cc ? `${cc.sym}${(price * rate).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : null;
    const r       = o.active ? rankOf(o) : null;
    const busy    = busyId === o.id;
    const hiddenSell = o.active && o.type === 'sell' && user && !user.verified;

    let comp = null;
    if (r) {
      if (r.rank === 1)                      comp = { cls: 'best', icon: <FaTrophy />, txt: 'Mejor precio del mercado' };
      else if (r.rank <= Math.ceil(r.total * 0.3)) comp = { cls: 'good', icon: <FaMedal />, txt: `Competitivo · #${r.rank} de ${r.total}` };
      else                                   comp = { cls: 'meh',  icon: <FaChartBar />, txt: `Mejorable · #${r.rank} de ${r.total}` };
    }

    return (
      <div className={`ml-card ${o.active ? '' : 'inactive'} ${isStale ? 'stale' : ''}`}>
        <div className="ml-card-main">
          <div className="ml-card-left">
            <CoinLogo symbol="USDT" size={40} className="ml-card-coin" />
            <div className="ml-info">
              <div className="ml-info-top">
                <span className={`fin-chip ml-badge ${o.type}`}>
                  {o.type === 'buy' ? <><FaShoppingCart /> Compra</> : <><FaMoneyBillWave /> Venta</>}
                </span>
                <strong className="ml-amount">USDT <span className="fin-num">{Number(o.amount).toLocaleString()}</span></strong>
              </div>
              <div className="ml-meta">
                <span className="fin-chip" style={{ color: CHAIN_COLORS[o.network] || 'var(--ml-text-secondary)', borderColor: (CHAIN_COLORS[o.network] || '#888') + '55' }}>
                  <ChainGlyph chain={o.network} /> {o.network}
                </span>
                <span className="fin-chip ml-pay-chip"><FaLink style={{ marginRight: 2, flexShrink: 0 }} /> {o.payment}</span>
                {(o.minAmount > 0 || o.maxAmount > 0) && (
                  <span className="fin-chip"><FaEye style={{ marginRight: 2 }} /> <span className="fin-num">{Number(o.minAmount).toLocaleString()}–{Number(o.maxAmount).toLocaleString()}</span></span>
                )}
                <span className="fin-chip"><FaClock style={{ marginRight: 2 }} /> <span className="fin-num">{o.responseTime}</span>m</span>
                <span className="ml-age fin-num"><FaClock style={{ marginRight: 3 }} /> {timeAgo(o.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* PRECIO + competitividad */}
          <div className="ml-price-box">
            <div className="ml-price-main">
              <span className="ml-price-unit">1 USDT</span>
              <span className="ml-price-val fin-num">${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
            </div>
            {local && <div className="ml-price-local fin-num">≈ {local}</div>}
            <div className="ml-price-foot">
              <span className={`ml-spread-tag ${o.spread > 0 ? 'up' : o.spread < 0 ? 'down' : 'trm'}`}>{spreadLabel(o.spread)}</span>
              {o.active && (
                <div className="ml-spread-step">
                  <button onClick={() => quickSpread(o, -0.5)} title="Bajar precio 0.5%"><FaMinus /></button>
                  <button onClick={() => quickSpread(o, +0.5)} title="Subir precio 0.5%"><FaPlus /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* fila inferior: competitividad + acciones */}
        <div className="ml-card-foot">
          <div className="ml-foot-left">
            {comp && <span className={`ml-comp ${comp.cls}`}>{comp.icon} {comp.txt}</span>}
            {hiddenSell && <span className="ml-comp warn"><FaExclamationTriangle /> Oculto: requiere verificación</span>}
            {!o.active && <span className="fin-chip ml-status inactive"><FaPause style={{ marginRight: 4 }} /> Pausado</span>}
          </div>
          <div className="ml-actions">
            {PRIVY_ENABLED && o.active && (
              <FeatureOfferButton offer={o} onFeatured={(u) => patchLocal(o.id, { featuredUntil: u?.featuredUntil })} />
            )}
            <button className="ml-act edit" onClick={() => setEditing(o)} title="Editar"><FaPen /> <span>Editar</span></button>
            {o.active ? (
              <button className="ml-act pause" onClick={() => setActive(o.id, false)} disabled={busy} title="Pausar">
                {busy ? <FaSpinner className="ml-spin" /> : <><FaPause /> <span>Pausar</span></>}
              </button>
            ) : (
              <button className="ml-act play" onClick={() => setActive(o.id, true)} disabled={busy} title="Activar">
                {busy ? <FaSpinner className="ml-spin" /> : <><FaPlay /> <span>Activar</span></>}
              </button>
            )}
            <button className="ml-act dup" onClick={() => duplicate(o)} title="Duplicar"><FaCopy /> <span>Duplicar</span></button>
            <button className="ml-act del" onClick={() => setConfirmDel(o.id)} title="Borrar"><FaTrashAlt /></button>
          </div>
        </div>

        {confirmDel === o.id && (
          <div className="ml-confirm">
            <span><FaTrashAlt style={{ marginRight: 6 }} /> ¿Borrar este anuncio? No se puede deshacer.</span>
            <div>
              <button className="ml-confirm-no" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="ml-confirm-yes" onClick={() => doDelete(o.id)} disabled={busy}>{busy ? '...' : 'Borrar'}</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ml-wrapper">
      <div className="ml-page">

        {/* HEADER */}
        <div className="ml-page-header">
          <div>
            <h1 className="ml-page-title"><FaTrophy style={{ marginRight: 12 }} /> Mis Anuncios</h1>
            <p className="ml-page-sub">Gestioná tus ofertas, ajustá precios y mirá cómo competís en el mercado.</p>
          </div>
          <div className="ml-header-actions">
            {cc && (
              <button className={`ml-cur-toggle ${showLocal ? 'on' : ''}`} onClick={() => setShowLocal(s => !s)} title="Mostrar en moneda local">
                {showLocal ? `${cc.sym} ${cc.code}` : '$ USD'}
              </button>
            )}
            <button className="ml-new-btn" onClick={() => navigate('/create')}><FaPlus style={{ marginRight: 6 }} /> Nuevo anuncio</button>
          </div>
        </div>

        {/* ALERTAS */}
        {visibleAlerts.map((a, i) => (
          <div key={i} className={`ml-alert ${a.type}`}>
            <span>{a.type === 'stale' ? <FaBell style={{ marginRight: 6 }} /> : <FaExclamationTriangle style={{ marginRight: 6 }} />} {a.msg}</span>
            <button className="ml-alert-dismiss" onClick={() => setDismissed(p => [...p, i])}><FaTimesCircle /></button>
          </div>
        ))}

        {/* STATS */}
        <div className="ml-stats">
          <div className="ml-stat">
            <div className="ml-stat-icon"><FaCheckCircle /></div>
            <div><strong className="fin-num">{stats.active}</strong><span className="fin-label">Activos</span></div>
          </div>
          <div className="ml-stat">
            <div className="ml-stat-icon inactive"><FaPause /></div>
            <div><strong className="fin-num">{stats.inactive}</strong><span className="fin-label">Pausados</span></div>
          </div>
          <div className="ml-stat highlight">
            <div className="ml-stat-icon"><CoinLogo symbol="USDT" size={20} /></div>
            <div><strong className="fin-num">${stats.totalVolume > 1000 ? (stats.totalVolume / 1000).toFixed(1) + 'K' : stats.totalVolume.toLocaleString()}</strong><span className="fin-label">Volumen publicado</span></div>
          </div>
          <div className="ml-stat">
            <div className="ml-stat-icon buy"><FaShoppingCart /></div>
            <div><strong className="fin-num">{offers.filter(o => o.type === 'buy').length}</strong><span className="fin-label">Compras</span></div>
          </div>
          <div className="ml-stat">
            <div className="ml-stat-icon sell"><FaMoneyBillWave /></div>
            <div><strong className="fin-num">{offers.filter(o => o.type === 'sell').length}</strong><span className="fin-label">Ventas</span></div>
          </div>
        </div>

        {/* VOLUME BAR */}
        {stats.totalVolume > 0 && (
          <div className="ml-volume-bar-wrap">
            <div className="ml-volume-bar">
              <div className="ml-volume-buy" style={{ width: `${(stats.buyVolume / stats.totalVolume) * 100}%` }} />
              <div className="ml-volume-sell" style={{ width: `${(stats.sellVolume / stats.totalVolume) * 100}%` }} />
            </div>
            <div className="ml-volume-labels">
              <span className="buy"><FaShoppingCart style={{ marginRight: 6 }} /> <span className="fin-num">${stats.buyVolume > 1000 ? (stats.buyVolume/1000).toFixed(1)+'K' : stats.buyVolume.toLocaleString()}</span></span>
              <span className="sell"><FaMoneyBillWave style={{ marginRight: 6 }} /> <span className="fin-num">${stats.sellVolume > 1000 ? (stats.sellVolume/1000).toFixed(1)+'K' : stats.sellVolume.toLocaleString()}</span></span>
            </div>
          </div>
        )}

        {/* FILTROS + ORDEN */}
        <div className="ml-controls">
          <div className="ml-filters">
            {[{ key: 'all', label: 'Todos', Icon: FaLink }, { key: 'buy', label: 'Compras', Icon: FaShoppingCart }, { key: 'sell', label: 'Ventas', Icon: FaMoneyBillWave }].map(f => {
              const FIcon = f.Icon;
              return (
                <button key={f.key} className={`ml-filter-btn ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
                  <FIcon style={{ marginRight: 6 }} /> {f.label}
                </button>
              );
            })}
          </div>
          <select className="ml-sort" value={sort} onChange={e => setSort(e.target.value)}>
            <option value="recent">Más recientes</option>
            <option value="price">Mejor precio (spread)</option>
            <option value="amount">Mayor monto</option>
          </select>
        </div>

        {/* CONTENIDO */}
        {loading ? (
          <div className="ml-empty"><span><FaSpinner className="ml-spin" /></span><p>Cargando anuncios...</p></div>
        ) : offers.length === 0 ? (
          <div className="ml-empty">
            <span><FaTrophy /></span>
            <p>No tenés anuncios creados aún.</p>
            <p className="ml-empty-sub">Publicá tu primer anuncio para empezar a operar en el marketplace.</p>
            <button className="ml-new-btn" onClick={() => navigate('/create')}><FaPlus style={{ marginRight: 6 }} /> Crear primer anuncio</button>
          </div>
        ) : (
          <div className="ml-list">
            <div className="ml-group-header"><span><FaFire style={{ marginRight: 6 }} /> Activos (<span className="fin-num">{active.length}</span>)</span></div>
            {active.length === 0
              ? <p className="ml-empty-group">No tenés anuncios activos con este filtro.</p>
              : active.map(o => <OfferCard key={o.id} o={o} />)}
            {inactive.length > 0 && (
              <div className="ml-inactive-section">
                <button className="ml-inactive-toggle" onClick={() => setShowInactive(p => !p)}>
                  {showInactive ? <FaChevronUp style={{ marginRight: 6 }} /> : <FaChevronDown style={{ marginRight: 6 }} />} <FaPause style={{ marginRight: 6 }} /> Pausados (<span className="fin-num">{inactive.length}</span>)
                </button>
                {showInactive && inactive.map(o => <OfferCard key={o.id} o={o} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {editing && (
        <EditModal
          offer={editing}
          busy={busyId === editing.id}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

// ── Modal de edición rápida ─────────────────────────────────────────
const PAYMENTS = ['Transferencia bancaria', 'SINPE Móvil', 'Nequi', 'Daviplata', 'PayPal', 'Wink', 'Mercado Pago', 'Efectivo', 'Zelle', 'Wise'];
const NETWORKS = ['BSC', 'TRON', 'Ethereum', 'Solana', 'Bitcoin', 'Litecoin'];

function EditModal({ offer, busy, onClose, onSave }) {
  const [f, setF] = useState({
    id: offer.id,
    amount: offer.amount, minAmount: offer.minAmount || 0, maxAmount: offer.maxAmount || 0,
    spread: offer.spread || 0, payment: offer.payment, responseTime: offer.responseTime || 5,
    network: offer.network,
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const price = effPrice(f.spread);

  return (
    <div className="ml-modal-overlay" onClick={onClose}>
      <div className="ml-modal" onClick={e => e.stopPropagation()}>
        <div className="ml-modal-head">
          <h3><FaPen style={{ marginRight: 8 }} /> Editar anuncio</h3>
          <button className="ml-modal-x" onClick={onClose}><FaTimesCircle /></button>
        </div>
        <div className="ml-modal-body">
          <label className="ml-f">
            <span>Monto total (USDT)</span>
            <input type="number" value={f.amount} min={0} onChange={e => set('amount', e.target.value)} />
          </label>
          <div className="ml-f-row">
            <label className="ml-f"><span>Mínimo (USDT)</span><input type="number" value={f.minAmount} min={0} onChange={e => set('minAmount', e.target.value)} /></label>
            <label className="ml-f"><span>Máximo (USDT)</span><input type="number" value={f.maxAmount} min={0} onChange={e => set('maxAmount', e.target.value)} /></label>
          </div>
          <label className="ml-f">
            <span>Precio / spread (%) — <strong className="fin-num">1 USDT = ${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</strong> ({spreadLabel(Number(f.spread))})</span>
            <input type="number" step="0.1" value={f.spread} onChange={e => set('spread', e.target.value)} />
            <small className="ml-f-hint">Negativo (−1%) = más barato, cerrás rápido. Positivo (+1%) = ganás más, tarda más. 0 = TRM (mercado).</small>
          </label>
          <div className="ml-f-row">
            <label className="ml-f"><span>Método de pago</span>
              <select value={f.payment} onChange={e => set('payment', e.target.value)}>
                {!PAYMENTS.includes(f.payment) && <option value={f.payment}>{f.payment}</option>}
                {PAYMENTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="ml-f"><span>Red</span>
              <select value={f.network} onChange={e => set('network', e.target.value)}>
                {!NETWORKS.includes(f.network) && <option value={f.network}>{f.network}</option>}
                {NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <label className="ml-f">
            <span>Tiempo de respuesta (min)</span>
            <input type="number" value={f.responseTime} min={1} onChange={e => set('responseTime', e.target.value)} />
          </label>
        </div>
        <div className="ml-modal-foot">
          <button className="ml-modal-cancel" onClick={onClose}>Cancelar</button>
          <button className="ml-modal-save" onClick={() => onSave(f)} disabled={busy}>
            {busy ? <FaSpinner className="ml-spin" /> : <><FaCheckCircle style={{ marginRight: 6 }} /> Guardar</>}
          </button>
        </div>
      </div>
    </div>
  );
}
